import React, { useEffect, useRef, useState } from 'react';
import { zh } from './i18n.js';
import { track } from './track.js';
import { call } from './api.js';
import { myLanguage } from './voice.js';
import { withReturn, hereNow } from './gate.jsx';
import { useCentre, marketFor, browseMarket, readMaxKm } from './place.js';
import { countLabel } from './rails.js';
import { VenueCard, TabBar, Pager, SearchBar, useSpeech, MicStatus } from './ui.jsx';
import { go } from './nav.js';
import * as rotate from './rotate.js';

/* ══════════════════════════════════════════════════════════════════════════
 * EXPLORE — search, filter, and the paged list
 *
 * From `explore.html`: the heading, the search placeholder, the four "what is
 * on" chips and the count line, all quoted rather than reworded.
 *
 *   heading      Explore
 *   placeholder  Search a dish, cuisine, restaurant or area
 *   chips        All · Happy Hour now · Last-Minute now · Game Day today
 *   count line   <b>n</b> place(s) for "…"
 *
 * ── THE SEARCH IS THE SERVER'S, NOT A FILTER OVER A LIST ────────────────────
 *
 * ⚠️ The locked screen holds a `DATA = [...]` array in the file and filters it
 * in the browser. That is why it can answer instantly and why it can never
 * ship: the array is seventy venues typed into a page. /discovery/search
 * resolves what was typed — an area, a cuisine, a dish, a venue name — and
 * says which it decided, so the screen never has to guess.
 *
 * ── AND IT SAYS WHEN IT CANNOT DECIDE ───────────────────────────────────────
 *
 * ⚠️ "KT" is Kennedy Town AND Kwun Tong. The resolver never picks one: it
 * answers `ambiguous_location` with the candidates, and this screen ASKS
 * rather than choosing. CLAUDE.md: *"The resolver never guesses."* A search
 * that silently picked Kwun Tong would send somebody across the harbour.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ `const MARKET = 'hong-kong'` WAS HERE. Al, 25 September 2026: *"A user
   registered under CA can physically be in Hong Kong and search Hong Kong."*
   Every search this screen has ever run asked the server for Hong Kong,
   whatever the pin said. `marketFor()` reads it off the active centre. */
const PAGE = 20;

/* ⚠️ THE FOUR CHIPS ARE THE LOCKED SCREEN'S, AND THREE OF THEM MEAN "NOW".
   `explore.html` writes "Happy Hour now", "Last-Minute now", "Game Day today"
   — a different promise from Home's rails, which say a venue HAS one. Here
   the Eleater is asking what they can walk into, so these filter on the deal
   type and the screen says so in the chip's own words. */
const CHIPS = [
  { key: 'all', label: 'All',             deal: null },
  { key: 'hh',  label: 'Happy Hour now',  deal: 'happy_hour' },
  { key: 'lms', label: 'Last-Minute now', deal: 'last_minute' },
  { key: 'gd',  label: 'Game Day today',  deal: 'game_day' },
];

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ THE SORT ROW — `explore.html #sorts`, AND IT WAS NOT BUILT AT ALL.
 *
 * The locked screen offers four orders. React offered none, so an Eleater who
 * wanted the nearest place, or the best rated, had no way to ask — the list
 * arrived in whatever order it arrived in and that was the end of it.
 *
 * ── ⚠️ EACH ONE APPEARS ONLY IF IT CAN BE HONESTLY ANSWERED ─────────────────
 *
 * The locked file's own rule, in its own words: *"Top rated needs a venue past
 * the five-review floor; Most liked feeds needs a heart. An order that cannot
 * change anything is a control that lies."*  So the row is built from what
 * came back, not from a fixed list of four buttons.
 *
 *   Best match   always — it is what the search already did
 *   Nearest      only when Eleat knows where the Eleater is
 *   Top rated    only when something in the results actually has a rating
 *
 * ⚠️⚠️ AND "MOST LIKED FEEDS" IS DELIBERATELY ABSENT — RAISED, NOT HIDDEN.
 * The locked screen has a fourth sort. `/discovery/search` does not support
 * it: its schema is `z.enum(['relevance','distance','rating'])` and the query
 * selects no heart count at all, so there is nothing to order by. Building the
 * button would be a control that lies — the exact thing the locked file's own
 * comment forbids two lines above. It needs a heart aggregate in the search
 * query and a fourth enum value, and that is Al's call, not a thing to slip in
 * while fixing a screen. **Flagged to him rather than shipped dead.**
 *
 * ⚠️ NEAREST ONLY WORKS BECAUSE THE POSITION NOW EXISTS. The route has read
 * `sort=distance` since it was written and quietly fell back to name order
 * without a point — so this button would have done nothing at all before
 * `place.js`. The two changes are one feature.
 * ══════════════════════════════════════════════════════════════════════════ */

const SORTS = [
  { key: 'relevance', label: 'Best match', can: () => true },
  /* ⚠️ ONLY WHEN ELEAT KNOWS WHERE THEY ARE. `sort=distance` without a point
     falls back to name order inside the route, silently — a button that
     re-sorts nothing. */
  { key: 'distance', label: 'Nearest', can: (_rows, near) => !!near },
  /* ⚠️ ONLY WHEN SOMETHING ACTUALLY HAS A RATING. A rating needs five eligible
     reviews; below that there is no number, so in a market Eleat has just
     opened this button would reorder nothing. */
  /* ⚠️ THE FIELD IS `rating`, AND `rating_shown` IS THE REAL TEST. The search
     returns `case when agg.meets_threshold then agg.rating_avg else null end
     as rating` — so a venue below the five-review floor comes back with null,
     exactly as every card already expects. Checking a column name that does
     not exist would have hidden this button for ever and looked deliberate. */
  { key: 'rating', label: 'Top rated', can: (rows) => rows.some((v) => v.rating != null) },
];

/* ⚠️ THE FILTERS A HOT KEY IS ALLOWED TO CARRY — the seven array filters plus
   the three single ones `/discovery/search` documents. `q`, `location_id`,
   `lat`/`lng`/`radius_km`, `sort`, `limit` and `offset` are deliberately NOT
   here: those belong to the search box, the area pill, the phone and the sort
   row, and a chip that could set them would be overruling a control the
   Eleater can see. `deal_type` IS here — "Happy hour in LKF" is a deal type
   and an area, and that is the whole point of the chip. */
const HOT_KEY_FILTERS = [
  'deal_type', 'location', 'cuisine', 'sub_cuisine', 'dining_format',
  'dietary_provision', 'venue_attribute', 'venue_type', 'price_band',
  'open_now', 'min_rating',
  /* 1 October 2026, migration 109 — the four filters all nine of Al's hot
     keys needed: any-of an attribute, open at a time tonight, new on Eleat,
     a game this weekend. Each is a FILTER, never a sort: the sort row stays
     the Eleater's own. */
  'venue_attribute_any', 'open_until', 'new_within_days', 'games_this_weekend',
];

export default function Explore() {
  /* ⚠️ HOME'S SEARCH BOX HANDS THE WORDS OVER — `?explore=1&q=…`, and a hot
     key tapped on Home arrives the same way. Eleat has ONE search, with the
     four tabs, the paging and the resolver behind it; Home growing its own
     is how this repository ended up with three of them. Seeding from the URL
     is what makes handing it over possible. */
  const [q, setQ] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('q') || ''; }
    catch { return ''; }
  });
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ AN AREA ARRIVES FROM THE LOCATION SCREEN — `?area=<uuid>&areaname=…`.

     Al's item 3: *"Hong Kong by district, Calgary by area."*  The areas are
     listed on the Location screen and each one lands HERE, filtered, because
     Eleat has ONE search with the four tabs, the paging and the resolver behind
     it. A second screen that lists venues would be the third search bar this
     repository has already paid for once.

     ⚠️ THE ID AND THE NAME TRAVEL TOGETHER, AND THE NAME IS ONLY EVER A LABEL.
     `location_id` is what the server filters on; `areaname` is what the pill
     says. Resolving the id back to a name would be a second request to draw one
     word, and using the NAME as the filter would put it through the resolver —
     where "KT" comes back ambiguous and Central has three spellings.

     ⚠️ AND IT IS STATE, NOT A CONSTANT READ FROM THE URL EACH RENDER, because
     it has to be REMOVABLE. A filter with no way off it is the Top rated dead
     end three notes below, which cost a reload to escape. */
  const [area, setArea] = useState(() => {
    try {
      const u = new URLSearchParams(window.location.search);
      const id = u.get('area');
      return id ? { id, name: u.get('areaname') || null } : null;
    } catch { return null; }
  });
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ A HOT KEY ARRIVES AS REAL SEARCH PARAMETERS — `?hk=1&hkname=…` plus
     whatever that chip actually filters on.

     Al, 30 September 2026: *"you've got to fix those hotkeys."*  They used to
     be handed over as `q`, and `q` resolves a restaurant NAME, an AREA or a
     CUISINE — so "Happy hour in LKF" went looking for a restaurant of that
     name, found none, and this screen drew an empty list. The WORDS and the
     SEARCH are two different things and they now travel as two different
     things: `hkname` is only ever the label on the pill.

     ⚠️ THE KEYS ARE WHITELISTED HERE TOO, not just on the server. This screen
     forwards them into a URL, and forwarding whatever happens to be in the
     address bar is how a screen becomes a way to send arbitrary parameters at
     the API. The list is the filters `/discovery/search` documents, minus the
     ones this screen already owns — `q`, `location_id` and the point are the
     search box, the area pill and the phone, and a chip must not fight them.

     ⚠️ AND IT IS STATE, SO IT COMES OFF IN ONE TAP, for the same reason the
     area pill is: a narrowed list that cannot be un-narrowed is the Top rated
     dead end, which cost a reload to escape. */
  const [hot, setHot] = useState(() => {
    try {
      const u = new URLSearchParams(window.location.search);
      if (u.get('hk') !== '1') return null;
      const params = {};
      for (const k of HOT_KEY_FILTERS) if (u.get(k)) params[k] = u.get(k);
      if (!Object.keys(params).length) return null;
      return { name: u.get('hkname') || null, params };
    } catch { return null; }
  });
  /* ⚠️ SEEDED FROM THE ADDRESS, AND WRITTEN BACK BELOW — Al, 7 Oct 2026:
     back from a restaurant must land on the same search, tab and order. */
  const fromUrl = (k, d) => {
    try { return new URLSearchParams(window.location.search).get(k) || d; } catch { return d; }
  };
  const [chip, setChip] = useState(() => fromUrl('chip', 'all'));
  const [page, setPage] = useState(() => Number(fromUrl('page', 0)) || 0);
  const [sort, setSort] = useState(() => fromUrl('sort', 'relevance'));
  /* ⚠️ WHERE THEY ARE, SUBSCRIBED — so "Nearest" appears the moment Eleat
     finds them, and re-sorts if they walk somewhere else. */
  const centre = useCentre();
  const near = (centre && Number.isFinite(centre.lat))
    ? { lat: centre.lat, lng: centre.lng } : null;
  /* ⚠️ null MEANS A REAL PLACE ELEAT IS NOT IN — answered below, never
     silently swapped for the launch market. */
  /* ⚠️ THE SAME CITY HOME SHOWS — Al, 6 Oct 2026, in Shenzhen: Home browsed
     Hong Kong and Explore said "Eleat is not in this area yet". Outside every
     Eleat city both now browse the nearest one. */
  /* outside Eleat's cities nothing is picked for them (Al-Noor, 16 Oct 2026):
     null → "Eleat isn't in your location yet. Choose a city", as Home does */
  /* never blank outside Eleat's cities — the nearest city, as Home (Al-Noor, 30 Oct 2026) */
  const market = browseMarket(centre).market;
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ ZARYNA ANSWERS ABOVE THE RESULTS — THE GOOGLE BAR. Al, 1 October
     2026: "Zaryna is supposed to be exactly like what Google has with the
     microphone. That way you can either type things in or press the
     microphone and ask your questions in which Zaryna would be answering."
     Google puts its AI answer above the ordinary results; this does the same:
     her answer streams in at the top, the restaurants stay underneath.

     ⚠️ ASKED ONLY ON PURPOSE — a search handed over from Home's bar (typed or
     spoken) or Enter in this box. Never per keystroke, never for a hot key or
     an area pill: every ask is a call to the model, and a call per letter
     would be a bill per letter.
     ⚠️ SHE NEEDS AN ACCOUNT (Al, 30 Sep: keep that). A visitor gets the
     results and her own screen's line, "Sign in to ask Zaryna."
     ⚠️ A FAILURE DRAWS NOTHING — the results are right there, and a red line
     about the assistant above a working list would be noise.
     ══════════════════════════════════════════════════════════════════════ */
  /* ⚠️ HER ANSWER OPENS ON HER OWN SCREEN — Al-Noor, 15 Oct 2026: one flow,
     the same on Home, Explore and her screen ("why is it not clean like
     Google, Siri, Muse… we are all over the place"). Enter or the microphone
     asks her there; Back returns to this list with the words still in the
     box. Typing still filters the list here, as it always has. (Replaces her
     answer drawn above this list, 11 Oct.) */
  const askZaryna = (t, spoken) => {
    const s = String(t || '').trim();
    if (s) go(`?zaryna=1&from=explore&q=${encodeURIComponent(s)}${spoken ? '&spoken=1' : ''}`);
  };
  useEffect(() => {
    try {
      const u = new URLSearchParams(window.location.search);
      const put = (k, v, d) => { if (v && v !== d) u.set(k, String(v)); else u.delete(k); };
      /* a new search is no longer "other restaurants" — the one left out comes back */
      if (u.get('not') && q.trim() !== (u.get('q') || '').trim()) u.delete('not');
      put('q', q.trim(), ''); put('chip', chip, 'all'); put('sort', sort, 'relevance'); put('page', page, 0);
      history.replaceState(history.state, '', `?${u.toString()}`);
    } catch { /* the address bar is a convenience; the search runs either way */ }
  }, [q, chip, sort, page]);
  /* ⚠️ THE ELEATER'S OWN LIMIT, WHICH THIS SCREEN NEVER SENT. `max_distance_km`
     has been saved on the profile since migration 091 and read by nothing.
     It measures from `near` — the CENTRE — so browsing Calgary from Vancouver
     searches 8 km around Calgary, not 8 km around the phone. */
  const maxKm = readMaxKm();
  const [res, setRes] = useState(null);      // null = still asking
  const [err, setErr] = useState(null);

  /* ⚠️ ONE REQUEST IN FLIGHT, AND THE LAST ONE TYPED WINS. Without this, a
     fast typist's "din" can answer after "dim sum" and overwrite it — the
     screen shows results for a word the Eleater has already finished
     changing, and nothing looks broken enough to report. */
  const seq = useRef(0);

  useEffect(() => {
    /* nothing to search where Eleat has no venues — the notice below says so */
    if (!market) { setRes(null); setErr(null); return undefined; }
    const mine = ++seq.current;
    const deal = CHIPS.find((c) => c.key === chip).deal;
    /* ⚠️ A SHORT WAIT, SO A SEARCH IS NOT SENT PER KEYSTROKE. "dim sum" is
       seven requests without it, six of them already stale on arrival. */
    const t = setTimeout(() => {
      const url = `/discovery/search?market=${market}`
        + (q.trim() ? `&q=${encodeURIComponent(q.trim())}` : '')
        + (deal ? `&deal_type=${deal}` : '')
        /* ⚠️ `location_id`, WHICH `searchInput` HAS ACCEPTED SINCE IT WAS
           WRITTEN (`discovery.routes.js` line 52) — so no route changed and no
           migration was needed for any of this. The area narrows the search; it
           does not replace the words, the chip or the point, so "dim sum in
           Wan Chai, nearest first" is all four at once. */
        + (area ? `&location_id=${encodeURIComponent(area.id)}` : '')
        /* ⚠️ THE HOT KEY'S OWN FILTERS, BY THEIR REAL NAMES. Keys were
           whitelisted when the state was built, so nothing from the address
           bar reaches the API unchecked. A multi-value filter is already a
           comma list and `strArray` on the route splits it back apart —
           before 30 September it did not, and `venue_type=a,b,c` matched a
           venue whose type was the literal string "a,b,c", which is to say
           nothing, silently. */
        + Object.entries(hot?.params || {})
            /* a chip the Eleater tapped wins over the hot key's own offer —
               both went, deal_type twice, and the server answered 422
               "Validation failed" (audit, 7 Oct 2026) */
            .filter(([k]) => !(deal && k === 'deal_type'))
            .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join('')
        /* ⚠️ THE POINT GOES WITH EVERY SEARCH, not only when sorting by it.
           Without it the route cannot compute `distance_km`, so the cards
           could not show how far anything is even when the Eleater had not
           asked for the nearest — and its default order is "nearest first"
           when it has a point, which is the sensible thing for a person
           standing in a street. */
        + (near ? `&lat=${near.lat}&lng=${near.lng}` : '')
        /* ⚠️ ALL THREE OR NONE — the route reads `radius_km` alongside the
           point, and a radius with no point filters nothing. */
        + (near && maxKm ? `&radius_km=${maxKm}` : '')
        + `&sort=${sort}`
        + `&limit=${PAGE}&offset=${page * PAGE}`;
      call(url)
        .then((d) => {
          if (mine !== seq.current) return;
          setRes(d); setErr(null);
          /* ⚠️ THE SEARCH IS COUNTED ONCE IT HAS ANSWERED, and only when there
             were words — an empty query with a deal chip is browsing, not a
             search, and the registry requires search_query. Counting on the
             answer rather than the keystroke means "dim sum" is one search,
             not seven, and the stale ones above are never counted at all. */
          if (q.trim()) {
            track('search', {
              search_query: q.trim(),
              source_screen: 'explore',
              metadata: { results: (d && d.results && d.results.length) || 0 },
            });
          }
        })
        .catch((e) => { if (mine === seq.current) { setErr(e.message); setRes(null); } });
    }, q ? 220 : 0);
    return () => clearTimeout(t);
  }, [q, chip, page, sort, market, maxKm, area && area.id,
      /* ⚠️ THE PARAMETERS THEMSELVES, NOT THE OBJECT. `hot` is rebuilt on
         every render, so depending on it would re-run the search for ever;
         depending on `hot && true` would never re-run it when the pill is
         cleared for a different one. The serialised filters are the fact that
         actually changed. */
      JSON.stringify(hot?.params || null),
      near && near.lat, near && near.lng]);

  /* typing or changing the chip starts again at page one — staying on page
     four of a search nobody is running any more shows an empty list */
  const onQ = (v) => { setQ(v); setPage(0); };
  /* the microphone, wired as Home's is (useSpeech): the words go in the box
     and are searched; Zaryna is asked as for a typed search */
  const [micSaid, setMicSaid] = useState(null);
  const speech = useSpeech({
    onPartial: (t) => { setMicSaid(null); onQ(t); },
    onDone: (t) => { if (t && t.trim()) { onQ(t); askZaryna(t, true); } },
    onQuiet: () => setMicSaid({ text: "I didn't catch that. Try again, or type it.", bad: false }),
    onError: (e) => setMicSaid({ text: e.message, bad: true }),
  });
  const onChip = (k) => { setChip(k); setPage(0); };
  /* ⚠️ A NEW ORDER STARTS AT PAGE ONE — the locked file says why: *"Page four
     of Top rated is not page four of Best match, and landing there means the
     best-rated places are behind you."* */
  const onSort = (k) => { setSort(k); setPage(0); };
  /* ⚠️ AND CLEARING THE AREA STARTS AT PAGE ONE TOO. Page three of Wan Chai is
     not page three of the whole city. Also drops it from the address bar, so a
     reload — or the back button — does not silently put the filter back on. */
  /* ⚠️ AND THE HOT KEY COMES OFF THE SAME WAY, address bar included. Every one
     of its own keys is removed by name — a blanket `?` reset would also throw
     away the area pill and the words in the box, and the two filters are
     independent: "Cocktails after work" narrowed to Wan Chai is a real thing
     somebody did on purpose. */
  const clearHot = () => {
    const keys = Object.keys(hot?.params || {});
    setHot(null);
    setPage(0);
    try {
      const u = new URLSearchParams(window.location.search);
      u.delete('hk'); u.delete('hkname');
      for (const k of keys) u.delete(k);
      history.replaceState(history.state, '', `?${u.toString()}`);
    } catch { /* an address bar that will not update never breaks a search */ }
  };
  const clearArea = () => {
    setArea(null);
    setPage(0);
    try {
      const u = new URLSearchParams(window.location.search);
      u.delete('area'); u.delete('areaname');
      history.replaceState(history.state, '', `?${u.toString()}`);
    } catch { /* the filter is off either way; the address bar is cosmetic */ }
  };

  /* `not=` — the restaurant they could not book, left out of "See other
     restaurants nearby" (Reserve.jsx, 12 Oct 2026) */
  const notId = (() => { try { return new URLSearchParams(window.location.search).get('not') || ''; } catch { return ''; } })();
  const allRows = (res && res.results) || [];
  const rows = notId ? allRows.filter((x) => x.id !== notId) : allRows;
  const dropped = allRows.length - rows.length;

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ AN ORDER THAT VANISHES MUST NOT LEAVE THE ELEATER STUCK IN IT.

     `explore.html` guards this in one line —
     `if(!sdefs.some(function(s){ return s[0]===SORT; })) SORT='relevance';`
     — and React had the filter without the reset.

     **It became a dead end the moment "Top rated" started EXCLUDING** (see
     `search.service.js`, same day): pick Top rated, then search something none
     of the rated venues match, and the results come back empty → nothing has a
     rating → the Top rated button is filtered out of the row → and with
     `rows.length > 1` false the whole sort row disappears. `sort` is still
     `rating`, so **every later search stays filtered, with no control on screen
     to undo it.** A reload was the only way out.

     ⚠️ IT SETTLES IN ONE STEP, not a loop: `relevance` can always be offered,
     so the reset can never fire twice.

     ⚠️ AND IT WAITS FOR AN ANSWER — `res.status === 'ok'`. Resetting while the
     first request is still out would throw away a sort the Eleater chose
     deliberately before anything had come back. */
  useEffect(() => {
    if (!res || res.status !== 'ok') return;
    const stillOffered = SORTS.some((s) => s.key === sort && s.can(rows, near));
    if (!stillOffered) setSort('relevance');
  }, [res, sort, near]);   // eslint-disable-line react-hooks/exhaustive-deps
  const total = res && typeof res.total === 'number' ? res.total - dropped : null;
  const pages = total != null ? Math.ceil(total / PAGE) : 0;

  return (
    <div className="home">
      <div className="top">
        <h1 className="heroline">Explore</h1>
        {/* ⚠️ HOME'S BAR, NOT A SECOND ONE — Al-Noor, 11 Oct 2026: "there also
            isn't a microphone in the search bar in explore… it's not even
            uniform." The same SearchBar (magnifier · words · microphone) and
            the same microphone states (MicStatus) as Home and Zaryna. */}
        <SearchBar className="xsearch"
          value={q} onChange={onQ}
          onSubmit={() => askZaryna(q)}
          placeholder="Search Eleat…"
          goLabel="Search"
          mic={speech.allowed
            ? { on: speech.hearing, onTap: speech.tap, label: 'Search by voice' }
            : null} />
        <MicStatus speech={speech} />
        {micSaid && (
          <p className={micSaid.bad ? 'err micsaid' : 'mut micsaid'} role="status">{micSaid.text}</p>
        )}
      </div>

      {/* ⚠️ THE FILTER IS ON SCREEN AND IT COMES OFF IN ONE TAP. A narrowed
          list that does not say it is narrowed reads as Eleat having almost no
          restaurants — and this one can cut a city down to four cards. The ✕ is
          the whole reason this is state and not a URL read. */}
      {/* ⚠️ THE HOT KEY SAYS ITS OWN NAME AND COMES OFF IN ONE TAP — same
          control as the area pill below, deliberately, because it is the same
          job: a list narrowed by something the Eleater cannot see is a list
          that reads as Eleat having almost nothing. "Cocktails after work"
          cuts Hong Kong to eight cards.
          ⚠️ AND THE LABEL IS THE CHIP'S OWN WORDS OFF `home_hot_keys.label`,
          carried in the URL rather than rebuilt here from the parameters —
          turning `{"venue_type":["cocktail_bar","bar"]}` back into English
          would be Eleat inventing copy for a row Al wrote (RULE 4). */}
      {hot && (
        <div className="areapill">
          <span>{hot.name || 'Filtered'}</span>
          <button type="button" onClick={clearHot}
            aria-label="Clear this filter">✕</button>
        </div>
      )}

      {area && (
        <div className="areapill">
          <span>{area.name || 'This area'}</span>
          {/* ⚠️ THE LABEL NAMES NO CITY, DELIBERATELY. This was written as
              `Show all of ${cityLabel(market)}` and `cityLabel` does not exist
              anywhere in Eleat — I invented a helper to fill a sentence, which
              is the second time in one hour (`cityName` on the Location screen
              was the first) and is RULE 4 in its smallest form. `market` here
              is a SLUG, so even reading it would have had a screen reader say
              "show all of hong-kong". */}
          <button type="button" onClick={clearArea}
            aria-label="Show every area">✕</button>
        </div>
      )}

      <div className="chipbox" role="group" aria-label="What is on">
        {CHIPS.map((c) => (
          <button key={c.key} type="button"
            className={c.key === chip ? 'chip on' : 'chip'}
            aria-pressed={c.key === chip}
            onClick={() => onChip(c.key)}>{c.label}</button>
        ))}
      </div>

      {/* ⚠️ BUILT FROM WHAT CAME BACK, NOT FROM A FIXED LIST OF BUTTONS —
          see the note above. An order that cannot change anything is a
          control that lies, and the locked file says so in those words. */}
      {/* …and it stays while a sort other than Best match is on, however few
          came back (16 Oct 2026, the walk: Top rated left one place, the row
          vanished, and nothing on screen could undo it — the same dead end). */}
      {res && res.status === 'ok' && (rows.length > 1 || sort !== 'relevance') && (
        <div className="sortrow" role="group" aria-label="Order">
          {SORTS.filter((sdef) => sdef.can(rows, near)).map((sdef) => (
            <button key={sdef.key} type="button"
              className={sdef.key === sort ? 'on' : ''}
              aria-pressed={sdef.key === sort}
              onClick={() => onSort(sdef.key)}>{sdef.label}</button>
          ))}
        </div>
      )}

      <main>
        {err && <p className="err">{err}</p>}
        {/* ⚠️ THE LOCATION SCREEN'S OWN WORDS — RULE 4, not new copy. Explore
            used to search Hong Kong from anywhere on earth and present the
            results as if they were where you were standing. */}
        {market === null && (
          <p className="mut">
            {"Eleat isn't in your location yet."}{' '}
            <a className="link" href={withReturn('?location=1', hereNow())}>Choose a city</a>
          </p>
        )}
        {market !== null && !res && !err && <p className="mut">Searching…</p>}

        {/* ⚠️ AMBIGUOUS MEANS ASK, NEVER PICK. KT is Kennedy Town and Kwun
            Tong; the server hands back both and the Eleater chooses. */}
        {res && res.status === 'ambiguous_location' && (
          <div className="askwrap">
            <p className="mut">Which one do you mean?</p>
            <div className="chipbox">
              {(res.options || []).map((o) => (
                <button key={o.id} type="button" className="chip"
                  onClick={() => onQ(o.name_en || o.name)}>
                  {zh(o.name_en, o.name_zh) || o.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {res && res.status === 'unknown_location' && (
          <p className="mut">We don’t know “{res.query}” yet.</p>
        )}

        {res && res.status === 'ok' && (
          <>
            {/* the locked screen's count line, word for word */}
            <div className="cntline">
              <b>{countLabel(total ?? rows.length)}</b>
              {` place${(total ?? rows.length) === 1 ? '' : 's'}`}
              {q.trim() ? ` for “${q.trim()}”` : ''}
              {/* the "· read as menu_dietary" note is gone — the server's own
                  key names on a screen, words the locked explore.html never
                  had (audit, 7 Oct 2026) */}
            </div>

            {/* ⚠️ THE LOCKED WORDS. `explore.html` says "No places match your
                search." — React had invented a longer line that told the
                Eleater what to try instead. RULE 4: it is not my copy.

                ⚠️⚠️ AND THIS COMMENT WAS INSIDE THE BRACKETS AND BROKE THE
                BUILD. `{​/* … *​/}` is how you write a comment where JSX
                CHILDREN go. Inside `{cond && ( … )}` the brackets are an
                EXPRESSION, and a second `{` there is read as the start of an
                object — so esbuild stopped at "Expected ) but found
                className". Above the line, not inside it. */}
            {rows.length === 0 && (
              <p className="mut">No places match your search.</p>
            )}

            {/* ⚠️ HELD ON THE FOUR-HOUR CLOCK. Al: *"It's only explore that
                has a four hour turnaround."*  A list somebody is searching is
                not a shop window — it has to still be there when they come
                back. ⚠️ The key carries the chip and the page, so changing
                the filter or turning a page is not mistaken for a new turn. */}
            <div className="grid">
              {/* ⚠️ ONLY THE DEFAULT ORDER TURNS. Al, 6 Oct 2026: "when i click
                  nearest nothing changes" — the turn was laid over Nearest and
                  Top rated too, so the order he chose was never the one shown. */}
              {(sort === 'relevance' ? rotate.held(`explore-${chip}-${page}`, rows) : rows)
                .map((v) => <VenueCard key={v.id} v={v} />)}
            </div>

            {pages > 1 && (
              <Pager page={page} pages={pages} onGo={setPage} />
            )}
          </>
        )}
      </main>

      <TabBar active="explore" />
    </div>
  );
}

